import type { Indicator, IndicatorCalcCallback, KLineData } from 'klinecharts'

/**
 * Indicators recalculated for the newest bar only, while the rest of the list stands.
 *
 * klinecharts recalculates EVERY indicator over the WHOLE data list on every `updateData` — and a
 * walkthrough replay calls it up to ~30 times a bar (the forming bar's frames, each reveal) over a
 * canvas that keeps everything the user paged in to the left. That is the per-frame cost this
 * bounds: when only the last bar changed, the wrapped calc runs over a recent window and writes
 * that one value into the previous result.
 *
 * "Only the last bar changed" is read off the engine's own bookkeeping: an `updateData` mutates
 * the data array in place (the last bar replaced, or one pushed), while every reload, page and
 * Heikin Ashi flip hands the engine a NEW array. So the same array, the same length or one more,
 * the same params and a previous result to extend — anything else runs the full calc.
 */

/**
 * Indicators whose value at a bar depends only on recent bars: bounded look-back windows, or
 * smoothing that forgets its seed (EMA-style) well within the warm-up. Path-dependent and
 * cumulative ones (SAR, OBV, PVT, AVP) are left out: computed over a window, they would jump.
 */
export const TAIL_SAFE_INDICATORS = new Set([
  'MA', 'EMA', 'SMA', 'BBI', 'BOLL', 'MACD', 'RSI', 'KDJ', 'CCI', 'DMI', 'WR',
  'BIAS', 'BRAR', 'CR', 'DMA', 'TRIX', 'VR', 'MTM', 'EMV', 'PSY', 'AO', 'ROC',
])

/** Bars a window reaches back at least; more for long periods (10× the largest param). */
const MIN_WARMUP = 1000

const BASE = Symbol('tailIndicatorCalc.base')

type Calc = IndicatorCalcCallback<unknown> & { [BASE]?: IndicatorCalcCallback<unknown> }

interface Memo {
  list: KLineData[]
  len: number
  params: string
}

const memo = new WeakMap<object, Memo>()

/** The calc a wrapper wraps — what a cloned indicator must register, so it is never wrapped twice. */
export function unwrapCalc<D>(calc: IndicatorCalcCallback<D>): IndicatorCalcCallback<D> {
  return ((calc as Calc)[BASE] as IndicatorCalcCallback<D> | undefined) ?? calc
}

function warmupFor(calcParams: unknown[] | undefined): number {
  const largest = Math.max(0, ...(calcParams ?? []).filter((p): p is number => typeof p === 'number' && Number.isFinite(p)))
  return Math.max(MIN_WARMUP, Math.ceil(10 * largest))
}

export interface TailCalcOptions {
  /** Bars the window must reach back for these calcParams (the library passes 10 × the def's lookback). Never below MIN_WARMUP. */
  warmup?: (calcParams: unknown[] | undefined) => number
}

export function withTailCalc<D>(calc: IndicatorCalcCallback<D>, opts: TailCalcOptions = {}): IndicatorCalcCallback<D> {
  const base = unwrapCalc(calc)
  const window_ = (calcParams: unknown[] | undefined) =>
    opts.warmup ? Math.max(MIN_WARMUP, Math.ceil(opts.warmup(calcParams))) : warmupFor(calcParams)
  const wrapped = ((dataList: KLineData[], indicator: Indicator<D>) => {
    const len = dataList.length
    const params = JSON.stringify(indicator.calcParams ?? [])
    const m = memo.get(indicator)
    const prev = indicator.result
    const tailOnly =
      !!m &&
      len > 0 &&
      m.list === dataList &&
      m.params === params &&
      (len === m.len || len === m.len + 1) &&
      Array.isArray(prev) &&
      prev.length === m.len
    memo.set(indicator, { list: dataList, len, params })
    if (!tailOnly) return base(dataList, indicator)

    const window = dataList.slice(Math.max(0, len - 1 - window_(indicator.calcParams)))
    const extend = (tail: D[]): D[] => {
      prev[len - 1] = tail[tail.length - 1]
      return prev
    }
    const tail = base(window, indicator)
    return Array.isArray(tail) ? extend(tail) : tail.then(extend)
  }) as IndicatorCalcCallback<D>
  ;(wrapped as Calc)[BASE] = base as IndicatorCalcCallback<unknown>
  return wrapped
}
